import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../../seo/Seo';
import { softwareSchema } from '../../seo/schema';
import Icon from '../home/Icon';
import AIScenes from './AIScenes';
import {
  CAPABILITY_GROUPS, AI_TASKS, AI_GUARDRAILS, AI_CONTROLS, AI_FAQS,
  AI_PRICE, SEAT_QUESTIONS, SEAT_ACTIONS,
} from './aiData';
import './RayaAI.css';

const STEP_MS = 620;

const TONES = {
  ok: ['var(--raya-success)', 'var(--raya-success-bg)'],
  info: ['var(--raya-info)', 'var(--raya-info-bg)'],
};

const useReveal = () => {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.ra-reveal'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    const failsafe = setTimeout(() => els.forEach((el) => el.classList.add('in')), 1200);
    return () => { io.disconnect(); clearTimeout(failsafe); };
  }, []);
};

/* ─── The result Raya AI leaves behind ───────────────────────────────────── */

const ResultCard = ({ result, visible }) => {
  const [fg, bg] = TONES[result.badge[1]] || TONES.info;
  return (
    <div className={`ra-result ${visible ? 'is-in' : ''}`}>
      <div className="ra-result-head">
        <span className="ra-result-title">{result.title}</span>
        <span className="ra-result-badge" style={{ color: fg, background: bg }}>{result.badge[0]}</span>
      </div>

      {result.kind === 'doc' && (
        <div className="ra-result-fields">
          {result.fields.map(([label, value]) => (
            <div className="ra-result-field" key={label}>
              <span className="ra-result-label">{label}</span>
              <span className="ra-result-value">{value}</span>
            </div>
          ))}
        </div>
      )}

      {result.kind === 'table' && (
        <div className="ra-result-table">
          <div className="ra-result-trow is-head">
            {result.cols.map((c, i) => <span key={c || i}>{c}</span>)}
          </div>
          {result.rows.map((row) => (
            <div className="ra-result-trow" key={row[0]}>
              {row.map((cell, i) => <span key={i} className={i === 0 ? 'ra-result-tlabel' : ''}>{cell}</span>)}
            </div>
          ))}
        </div>
      )}

      <div className="ra-result-total">
        <span>{result.total[0]}</span>
        <strong>{result.total[1]}</strong>
      </div>
      <p className="ra-result-note"><Icon name="info" size={13} />{result.note}</p>
    </div>
  );
};

/* ─── The conversation demo ──────────────────────────────────────────────── */

const Conversation = () => {
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const timers = useRef([]);
  const task = AI_TASKS[active];

  // Each task replays from the top: steps land one by one, then the record.
  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setShown(0);
    const total = task.steps.length + 1; // steps, then the result
    for (let i = 1; i <= total; i += 1) {
      timers.current.push(setTimeout(() => setShown(i), STEP_MS * i));
    }
    return () => timers.current.forEach(clearTimeout);
  }, [task]);

  const done = shown > task.steps.length;

  return (
    <div className="ra-demo">
      <div className="ra-demo-tabs" role="tablist" aria-label="What to ask Raya AI">
        {AI_TASKS.map((t, i) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={i === active}
            className={`ra-demo-tab ${i === active ? 'is-on' : ''}`}
            onClick={() => setActive(i)}
            style={i === active ? { '--tab': t.color, '--tab-tint': t.tint } : undefined}
          >
            <Icon name={t.icon} size={15} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="ra-demo-frame">
        <div className="ra-demo-window">
          <div className="ra-demo-bar">
            <span className="ra-demo-crumb">Raya &rsaquo; Ask Raya AI</span>
            <span className="ra-demo-live"><span className="ra-demo-dot" />Working on live data</span>
          </div>

          <div className="ra-demo-body">
            {/* What you say */}
            <div className="ra-msg ra-msg--you">
              <span className="ra-msg-who">You</span>
              <p className="ra-msg-text">{task.prompt}</p>
            </div>

            {/* What it does */}
            <div className="ra-msg ra-msg--ai">
              <span className="ra-msg-who ra-msg-who--ai">
                <Icon name="sparkles" size={13} />Raya AI
              </span>
              <ul className="ra-steps">
                {task.steps.map((step, i) => (
                  <li key={step} className={shown > i ? 'is-in' : ''}>
                    <span className="ra-step-tick" aria-hidden="true">
                      {shown > i ? '✓' : ''}
                    </span>
                    {step}
                  </li>
                ))}
              </ul>

              {!done && <div className="ra-typing" aria-hidden="true"><span /><span /><span /></div>}
              {done && <ResultCard result={task.result} visible={done} />}
            </div>
          </div>
        </div>
      </div>

      <p className="ra-demo-foot">
        Illustrative data. Every figure here would be your own — Raya AI reads and writes the same
        records your team does.
      </p>
    </div>
  );
};

/* ─── Page ───────────────────────────────────────────────────────────────── */

const RayaAIPage = () => {
  const navigate = useNavigate();
  useReveal();

  return (
    <div className="ra-page">
      <Seo
        path="/ai"
        faqs={AI_FAQS}
        schema={[softwareSchema({ name: 'Raya AI', subCategory: 'AI assistant for ERP', path: '/ai', description: 'An AI assistant inside Raya ERP that drafts entries, reconciles bank statements, finds stock and forecasts demand, with every draft approved by a person.' })]}
      />

      {/* ── Hero ─────────────────────────────── */}
      <section className="ra-hero">
        <div className="ra-hero-grid" aria-hidden="true" />
        <div className="ra-hero-blob ra-hero-blob--violet" aria-hidden="true" />
        <div className="ra-hero-blob ra-hero-blob--mint" aria-hidden="true" />
        <div className="ra-container ra-hero-inner">
          <span className="ra-hero-badge"><Icon name="sparkles" size={14} />Raya AI</span>
          <h1 className="ra-hero-title">Tell AI what to do. <em>AI does the work.</em></h1>
          <p className="ra-hero-sub">
            Most business AI explains your data back to you. Raya AI fills in the bill, the expense,
            the invoice and the quotation — finding the vendor, matching the PO, working out the GST —
            then hands the draft to you. AI reconciles your bank statement, finds stock across every
            warehouse and forecasts what next month needs. Nothing reaches your books without a person.
          </p>
          <div className="ra-hero-actions">
            <button type="button" className="ra-btn ra-btn--primary ra-btn--lg" onClick={() => navigate('/register')}>Start free trial</button>
            <button type="button" className="ra-btn ra-btn--ghost ra-btn--lg" onClick={() => navigate('/pricing')}>See pricing →</button>
          </div>
          <p className="ra-hero-meta">Seats you assign &middot; drafts you approve</p>
        </div>
      </section>

      {/* ── The scroll reel ─────────────────────────────── */}
      <section className="ra-reel">
        <div className="ra-container ra-reel-head">
          <span className="ra-eyebrow ra-eyebrow--light">Watch AI work</span>
          <h2 className="ra-h2 ra-h2--light">Paper, statements and history — <em>turned into entries.</em></h2>
          <p className="ra-lead ra-lead--center ra-lead--light">
            Three jobs your team does by hand every month. Scroll, and watch what AI does with each.
          </p>
        </div>
        <AIScenes />
      </section>

      {/* ── The demo ─────────────────────────────── */}
      <section className="ra-section ra-section--alt">
        <div className="ra-container">
          <div className="ra-head ra-head--center ra-reveal">
            <span className="ra-eyebrow">Ask, and it is done</span>
            <h2 className="ra-h2">One sentence in. <em>A finished document out.</em></h2>
            <p className="ra-lead ra-lead--center">
              Pick a job below and watch what AI actually does with it — the masters AI matches, the tax
              AI works out, and the document AI puts in front of you to approve.
            </p>
          </div>
          <div className="ra-reveal"><Conversation /></div>
        </div>
      </section>

      {/* ── Capabilities ─────────────────────────────── */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="ra-head ra-reveal">
            <span className="ra-eyebrow">What you can hand it</span>
            <h2 className="ra-h2">Four kinds of work, <em>one way of asking.</em></h2>
            <p className="ra-lead">
              No commands to learn and no syntax to remember. Say it the way you would say it to
              someone on your team.
            </p>
          </div>
          <div className="ra-caps ra-reveal">
            {CAPABILITY_GROUPS.map((g) => (
              <article className="ra-cap" key={g.key} style={{ '--cap': g.color }}>
                <span className="ra-cap-icon" style={{ background: g.tint, color: g.color }}>
                  <Icon name={g.icon} size={20} />
                </span>
                <h3 className="ra-cap-title">{g.title}</h3>
                <p className="ra-cap-blurb">{g.blurb}</p>
                <ul className="ra-cap-list">
                  {g.items.map((it) => (
                    <li key={it}><span className="ra-tick" aria-hidden="true">✓</span>{it}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why it can be trusted ─────────────────────────────── */}
      <section className="ra-section ra-section--dark">
        <div className="ra-container">
          <div className="ra-head ra-head--center ra-reveal">
            <span className="ra-eyebrow ra-eyebrow--light">The part that matters</span>
            <h2 className="ra-h2 ra-h2--light">AI with <em>the keys you gave it</em> — and no others.</h2>
            <p className="ra-lead ra-lead--center ra-lead--light">
              Letting software near your books is a question of control, not cleverness. Here is exactly
              how far AI can go, and where AI stops.
            </p>
          </div>
          <div className="ra-guards ra-reveal">
            {AI_GUARDRAILS.map((g) => (
              <div className="ra-guard" key={g.title}>
                <span className="ra-guard-icon"><Icon name={g.icon} size={17} /></span>
                <div>
                  <h3 className="ra-guard-title">{g.title}</h3>
                  <p className="ra-guard-body">{g.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Admin controls ─────────────────────────────── */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="ra-head ra-reveal">
            <span className="ra-eyebrow">Yours to configure</span>
            <h2 className="ra-h2">Every dial is <em>on your side of the glass.</em></h2>
            <p className="ra-lead">
              Who gets a seat, what it may draft, what gets logged and what it all costs — set from your
              own Settings and Usage screens, changed whenever you like, without asking us.
            </p>
          </div>
          <div className="ra-controls ra-reveal">
            {AI_CONTROLS.map((c) => (
              <article className="ra-control" key={c.key}>
                <header className="ra-control-head">
                  <span className="ra-control-icon"><Icon name={c.icon} size={18} /></span>
                  <span className="ra-control-tab">{c.tab}</span>
                </header>
                <h3 className="ra-control-title">{c.title}</h3>
                <p className="ra-control-blurb">{c.blurb}</p>
                <ul className="ra-control-list">
                  {c.points.map((pt) => (
                    <li key={pt}><span className="ra-tick" aria-hidden="true">✓</span>{pt}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────── */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="ra-price ra-reveal">
            <div className="ra-price-text">
              <span className="ra-eyebrow">What it costs</span>
              <h2 className="ra-h2">Everyone gets a taste. <em>Add it where it earns its keep.</em></h2>
              <p className="ra-lead">
                Seats are priced individually, so you give them to the owners and the main operating
                accounts rather than the whole team. Each seat tops up a pool everybody shares, which means
                a heavy user and a light one balance each other out instead of you buying for the peak.
              </p>
              <div className="ra-price-actions">
                <button type="button" className="ra-btn ra-btn--primary" onClick={() => navigate('/pricing')}>See full pricing</button>
                <button type="button" className="ra-btn ra-btn--outline" onClick={() => navigate('/contact-us')}>Talk to sales</button>
              </div>
            </div>
            <div className="ra-price-card">
              <span className="ra-price-kind">Raya AI</span>
              <div className="ra-price-amount">+₹{AI_PRICE}</div>
              <span className="ra-price-unit">per AI seat, per month</span>
              <ul className="ra-price-list">
                <li><span className="ra-tick" aria-hidden="true">✓</span>Adds {SEAT_QUESTIONS} questions and {SEAT_ACTIONS} actions to the shared pool</li>
                <li><span className="ra-tick" aria-hidden="true">✓</span>Works across every module you own</li>
                <li><span className="ra-tick" aria-hidden="true">✓</span>Assign or remove a seat any month</li>
                <li><span className="ra-tick" aria-hidden="true">✓</span>Top-up packs when a month runs heavy</li>
              </ul>
              <p className="ra-price-fine">
                The pool is company-wide, resets on the 1st and does not roll over. Top-up packs are there
                for a month that runs heavy, so a busy week never means buying a seat you do not need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────── */}
      <section className="ra-section ra-section--alt">
        <div className="ra-container ra-container--narrow">
          <div className="ra-head ra-head--center ra-reveal">
            <span className="ra-eyebrow">Questions</span>
            <h2 className="ra-h2">The ones people actually ask.</h2>
          </div>
          <div className="ra-faq ra-reveal">
            {AI_FAQS.map((f) => (
              <details className="ra-faq-item" key={f.q}>
                <summary className="ra-faq-q">{f.q}<span className="ra-faq-plus" aria-hidden="true">+</span></summary>
                <p className="ra-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────── */}
      <section className="ra-cta">
        <div className="ra-cta-blob" aria-hidden="true" />
        <div className="ra-container ra-cta-inner">
          <h2 className="ra-cta-title">Stop typing what you could simply ask for.</h2>
          <div className="ra-cta-actions">
            <button type="button" className="ra-btn ra-btn--primary ra-btn--lg" onClick={() => navigate('/register')}>Start free trial</button>
            <button type="button" className="ra-btn ra-btn--outline ra-btn--lg" onClick={() => navigate('/contact-us')}>Book a demo</button>
          </div>
          <p className="ra-cta-meta">Raya Software Solutions · Built for Indian businesses</p>
        </div>
      </section>
    </div>
  );
};

export default RayaAIPage;
